import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../../services/ApiService';
import type { EntryMode, EventPricing } from '../../types/event';
import { isPaidEntryMode } from '../../utils/registrationStatus';
import Button from '../ui/Button';

interface EventPricingPanelProps {
  eventId: number;
  entryMode?: EntryMode | null;
  paidEntryFlagEnabled: boolean;
}

const EventPricingPanel: React.FC<EventPricingPanelProps> = ({
  eventId,
  entryMode,
  paidEntryFlagEnabled,
}) => {
  const paidMode = isPaidEntryMode(entryMode ?? null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [pricing, setPricing] = useState<EventPricing | null>(null);

  const [enabled, setEnabled] = useState(false);
  const [priceDollars, setPriceDollars] = useState('50.00');
  const [deadlineHours, setDeadlineHours] = useState('72');
  const [refundPolicy, setRefundPolicy] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getEventPricing(eventId);
      setPricing(data);
      if (data) {
        setEnabled(Boolean(data.paid_entry_enabled));
        if (data.vehicle_entry_price_cents != null) {
          setPriceDollars((data.vehicle_entry_price_cents / 100).toFixed(2));
        }
        if (data.payment_deadline_hours != null) {
          setDeadlineHours(String(data.payment_deadline_hours));
        }
        setRefundPolicy(data.refund_policy_text || '');
      } else if (paidMode) {
        setEnabled(true);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load pricing');
    } finally {
      setLoading(false);
    }
  }, [eventId, paidMode]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!paidEntryFlagEnabled && enabled) {
      setError('paid_vehicle_entry_enabled must be on in Admin → Payments first.');
      return;
    }
    const dollars = Number(priceDollars);
    if (enabled && (!Number.isFinite(dollars) || dollars <= 0)) {
      setError('Enter a vehicle entry price greater than $0.');
      return;
    }
    const hours = Math.max(1, Math.floor(Number(deadlineHours) || 72));

    try {
      setBusy(true);
      setError(null);
      setSuccess(null);
      const saved = await apiService.saveEventPricing({
        event_id: eventId,
        paid_entry_enabled: enabled,
        vehicle_entry_price_cents: Math.round(Math.max(0, dollars) * 100),
        currency: 'usd',
        payment_deadline_hours: hours,
        refund_policy_text: refundPolicy.trim() || null,
      });
      setPricing(saved);
      setSuccess(
        enabled
          ? 'Paid vehicle entry pricing saved. Builders can check out after approval (or immediately for open paid).'
          : 'Paid entry pricing disabled for this event.'
      );
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save pricing';
      if (message.includes('paid_entry_not_eligible')) {
        setError('Payout setup is not ready. Finish Connect onboarding first.');
      } else {
        setError(message);
      }
    } finally {
      setBusy(false);
    }
  };

  if (!paidEntryFlagEnabled && !paidMode) {
    return (
      <div className="form-section space-y-2">
        <h2 className="heading-section text-sm">Vehicle entry pricing</h2>
        <p className="text-sm text-gray-400 leading-relaxed">
          Paid vehicle entry is not enabled on this platform yet. A super-admin can turn on{' '}
          <span className="text-gray-300">paid_vehicle_entry_enabled</span> under Admin → Payments.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="form-section space-y-2">
        <h2 className="heading-section text-sm">Vehicle entry pricing</h2>
        <p className="text-sm text-gray-400 leading-relaxed">
          Set the entry fee builders pay after approval (curated) or at registration (open paid).
          Requires payout setup to be ready.
        </p>
        <Link to="/organizer/payments" className="text-orange-300 text-sm min-h-11 inline-flex items-center">
          Check payout setup →
        </Link>
        {paidMode && !pricing?.paid_entry_enabled ? (
          <p className="text-sm text-amber-300" role="status">
            This event uses a paid entry mode, but pricing is not enabled yet. Save a price below
            before checkout will work.
          </p>
        ) : null}
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Loading pricing…</p>
      ) : (
        <form onSubmit={handleSave} className="form-section">
          <label className="flex items-center gap-2 text-sm text-gray-300 min-h-11">
            <input
              type="checkbox"
              checked={enabled}
              disabled={!paidEntryFlagEnabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="checkbox-control"
            />
            Enable paid vehicle entry for this event
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="entry-price" className="input-label">
                Entry price (USD)
              </label>
              <input
                id="entry-price"
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                value={priceDollars}
                onChange={(e) => setPriceDollars(e.target.value)}
                disabled={!enabled}
                required={enabled}
                className="input"
              />
            </div>
            <div>
              <label htmlFor="payment-deadline" className="input-label">
                Payment deadline (hours after approval)
              </label>
              <input
                id="payment-deadline"
                type="number"
                inputMode="numeric"
                min="1"
                step="1"
                value={deadlineHours}
                onChange={(e) => setDeadlineHours(e.target.value)}
                disabled={!enabled}
                className="input"
              />
            </div>
          </div>

          <div>
            <label htmlFor="refund-policy" className="input-label">
              Refund policy text (shown to builders)
            </label>
            <textarea
              id="refund-policy"
              value={refundPolicy}
              onChange={(e) => setRefundPolicy(e.target.value)}
              disabled={!enabled}
              maxLength={1000}
              rows={3}
              className="input input-textarea"
              placeholder="e.g. Full refund until 7 days before the event."
            />
          </div>

          {error ? (
            <p className="text-sm text-red-400" role="alert">
              {error}
            </p>
          ) : null}
          {success ? (
            <p className="text-sm text-emerald-400" role="status">
              {success}
            </p>
          ) : null}

          <Button type="submit" variant="primary" loading={busy} className="min-h-[44px]">
            Save pricing
          </Button>
        </form>
      )}
    </div>
  );
};

export default EventPricingPanel;
